import { useCallback, useEffect, useState } from "react";
import { Camera, FileSpreadsheet, Loader2, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import DeliveryFormDialog from "@/components/DeliveryFormDialog";
import RevenuePanel from "@/components/RevenuePanel";
import PhotosDialog from "@/components/PhotosDialog";
import DateField from "@/components/DateField";
import { errMsg, useApi } from "@/lib/api";
import { eur, num } from "@/lib/format";
import { useMe } from "@/context/MeContext";

const ALL = "alle";

const Filter = ({ value, onChange, items, label, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="w-full sm:w-64" data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>
      <SelectItem value={ALL}>{label}</SelectItem>
      {items.map((i) => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}
    </SelectContent>
  </Select>
);

const DeleteBtn = ({ id, onConfirm }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild><Button variant="ghost" size="icon" data-testid={`btn-delete-delivery-${id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button></AlertDialogTrigger>
    <AlertDialogContent className="border-slate-200 bg-white">
      <AlertDialogHeader><AlertDialogTitle>Anlieferung löschen?</AlertDialogTitle></AlertDialogHeader>
      <AlertDialogFooter><AlertDialogCancel>Abbrechen</AlertDialogCancel><AlertDialogAction onClick={onConfirm} data-testid="btn-confirm-delete-delivery">Löschen</AlertDialogAction></AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export default function Anlieferungen() {
  const api = useApi();
  const me = useMe();
  const [rows, setRows] = useState(null);
  const [stats, setStats] = useState(null);
  const [yards, setYards] = useState([]);
  const [grades, setGrades] = useState([]);
  const [yardF, setYardF] = useState(ALL);
  const [gradeF, setGradeF] = useState(ALL);
  const [form, setForm] = useState(null);
  const [failed, setFailed] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [photos, setPhotos] = useState(null);
  const [exporting, setExporting] = useState(false);

  const params = { ...(yardF !== ALL && { yard_id: yardF }), ...(gradeF !== ALL && { grade_key: gradeF }), ...(from && { date_from: from }), ...(to && { date_to: to }) };
  const paramKey = JSON.stringify(params);

  const load = useCallback(async () => {
    try {
      const [d, s, y, g] = await Promise.all([api.get("/deliveries", { params: JSON.parse(paramKey) }), api.get("/deliveries/stats"), api.get("/yards"), api.get("/grades")]);
      setRows(d.data); setStats(s.data); setYards(y.data); setGrades(g.data); setFailed(false);
    } catch { setFailed(true); }
  }, [api, paramKey]);
  useEffect(() => { load(); }, [load]);

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const r = await api.get("/deliveries/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Anlieferungen_${new Date().toLocaleDateString("sv-SE")}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel-Datei erstellt");
    } catch { toast.error("Export fehlgeschlagen"); }
    setExporting(false);
  };

  const remove = async (d) => { try { await api.delete(`/deliveries/${d.id}`); toast.success("Gelöscht"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const canEdit = (d) => me.role === "admin" || d.created_by_id === me.user_id;

  if (failed && !rows) return <p className="py-16 text-center text-sm text-slate-500" data-testid="deliveries-error">Anlieferungen konnten nicht geladen werden. Bitte Seite neu laden.</p>;
  if (!rows) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-orange-600" /></div>;
  const sumKg = rows.reduce((a, r) => a + r.kg, 0);
  const sumRev = rows.reduce((a, r) => a + r.revenue, 0);

  return (
    <div className="space-y-6" data-testid="deliveries-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Anlieferungen</h1>
          <p className="mt-1 text-sm text-slate-500">Gewicht, Sorte und Erlös je Wertstoffhof erfassen.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={exportXlsx} disabled={exporting} data-testid="btn-export-xlsx">
            {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-2 h-4 w-4 text-emerald-700" />} Excel-Export
          </Button>
          <Button onClick={() => setForm({})} disabled={yards.length === 0} data-testid="btn-create-delivery"><Plus className="mr-2 h-4 w-4" /> Anlieferung erfassen</Button>
        </div>
      </div>
      <RevenuePanel stats={stats} showLink={false} />
      <div className="panel overflow-x-auto p-5">
        <div className="mb-4 flex flex-col flex-wrap gap-3 sm:flex-row sm:items-center">
          <Filter value={yardF} onChange={setYardF} label="Alle Wertstoffhöfe" items={yards.map((y) => ({ value: y.id, label: y.name }))} testId="filter-delivery-yard" />
          <Filter value={gradeF} onChange={setGradeF} label="Alle Sorten" items={grades.map((g) => ({ value: g.key, label: g.name }))} testId="filter-delivery-grade" />
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">von</span>
            <div className="w-36"><DateField value={from} onChange={setFrom} testId="filter-delivery-from" /></div>
            <span className="text-sm text-slate-500">bis</span>
            <div className="w-36"><DateField value={to} onChange={setTo} testId="filter-delivery-to" /></div>
            {(from || to) && <Button variant="ghost" size="icon" onClick={() => { setFrom(""); setTo(""); }} title="Zeitraum zurücksetzen" data-testid="btn-clear-dates"><X className="h-4 w-4" /></Button>}
          </div>
        </div>
        {rows.length === 0 ? <p className="py-8 text-center text-sm text-slate-500" data-testid="deliveries-empty">Noch keine Anlieferungen erfasst.</p> : (
          <table className="w-full min-w-[760px] text-sm" data-testid="table-deliveries">
            <thead className="text-left text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="pb-3">Datum</th><th className="pb-3">Wertstoffhof</th><th className="pb-3">Sorte</th><th className="pb-3 text-right">kg</th><th className="pb-3 text-right">€/kg</th><th className="pb-3 text-right">Erlös</th><th className="pb-3 pl-6">Erfasst von</th><th className="pb-3 text-center">Beleg</th><th /></tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.id} className="border-t border-slate-200" data-testid={`row-delivery-${d.id}`}>
                  <td className="py-3">{new Date(`${d.date}T00:00:00`).toLocaleDateString("de-DE")}</td>
                  <td className="py-3">{d.yard_name}</td>
                  <td className="py-3"><div>{d.grade_name}</div>{d.note && <div className="text-xs text-slate-500">{d.note}</div>}</td>
                  <td className="num py-3 text-right">{num(d.kg, 1)}</td>
                  <td className="num py-3 text-right text-slate-600">{eur(d.price_eur_kg, 3)}</td>
                  <td className="num py-3 text-right font-semibold text-orange-700">{eur(d.revenue)}</td>
                  <td className="max-w-[180px] truncate py-3 pl-6 text-slate-500">{d.created_by}</td>
                  <td className="py-3 text-center">
                    <Button variant="ghost" size="sm" onClick={() => setPhotos(d)} className={d.photo_count ? "text-orange-700" : "text-slate-400"} data-testid={`btn-photos-${d.id}`}>
                      <Camera className="h-4 w-4" />{d.photo_count ? <span className="num ml-1">{d.photo_count}</span> : null}
                    </Button>
                  </td>
                  <td className="whitespace-nowrap py-3 text-right">
                    {canEdit(d) && <><Button variant="ghost" size="icon" onClick={() => setForm(d)} data-testid={`btn-edit-delivery-${d.id}`}><Pencil className="h-4 w-4" /></Button><DeleteBtn id={d.id} onConfirm={() => remove(d)} /></>}
                  </td>
                </tr>
              ))}
              <tr className="border-t-2 border-slate-300 font-semibold" data-testid="deliveries-sum-row">
                <td className="py-3" colSpan={3}>Summe ({rows.length})</td>
                <td className="num py-3 text-right">{num(sumKg, 1)}</td>
                <td className="num py-3 text-right text-slate-600">{sumKg ? eur(sumRev / sumKg, 3) : "–"}</td>
                <td className="num py-3 text-right text-orange-700">{eur(sumRev)}</td>
                <td colSpan={3} />
              </tr>
            </tbody>
          </table>
        )}
      </div>
      {form && <DeliveryFormDialog delivery={form.id ? form : null} yards={yards} grades={grades} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />}
      {photos && <PhotosDialog delivery={photos} canEdit={canEdit(photos)} onClose={() => setPhotos(null)} onChanged={load} />}
    </div>
  );
}
